import React, { useState } from "react";
import { Download, Trash2, Upload, FileText } from "react-feather";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import {
  useGetEmployeeDocumentsQuery,
  useUploadEmployeeDocumentMutation,
  useDeleteEmployeeDocumentMutation,
  useLazyGetEmployeeDocumentContentQuery,
} from "../employeesApi";
import { useCanViewDocuments, useCanManageDocuments } from "../useEmployeePermissions";

// Fixed set, must match the API's own EmployeeDocumentTypes.Valid
// (Batch68) exactly - a mismatch here surfaces as a 400 on upload, not a
// silent UI bug, but keeping this list identical avoids the round-trip.
const DOCUMENT_TYPES = ["ID Copy", "Certificate", "Contract", "Medical", "Approval Signature", "Other"];

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

// Scanned document attachments (Batch68) - Employee Master screen only, per
// explicit instruction (Flutter never touches this). Disk + relative-path
// storage on the API side, but that's invisible here - this component only
// ever deals with metadata (list) and bytes fetched on explicit demand
// (view/download), same "never preload the content" discipline as
// Collections' attachment viewer.
const EmployeeDocuments = ({ recordGuid }) => {
  const canView = useCanViewDocuments();
  const canManage = useCanManageDocuments();

  const { data: documents = [], isLoading } = useGetEmployeeDocumentsQuery(recordGuid, { skip: !canView || !recordGuid });
  const [uploadDocument, { isLoading: isUploading }] = useUploadEmployeeDocumentMutation();
  const [deleteDocument] = useDeleteEmployeeDocumentMutation();
  const [fetchContent] = useLazyGetEmployeeDocumentContentQuery();

  const [documentType, setDocumentType] = useState(DOCUMENT_TYPES[0]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  if (!canView) return null;

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error("Choose a file first");
      return;
    }
    try {
      await uploadDocument({ recordGuid, documentType, file: selectedFile }).unwrap();
      toast.success("Document uploaded");
      setSelectedFile(null);
    } catch (error) {
      toast.error(error?.message || "Could not upload document");
    }
  };

  const handleDelete = async (documentRecordGuid) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await deleteDocument({ recordGuid, documentRecordGuid }).unwrap();
      toast.success("Document removed");
    } catch (error) {
      toast.error(error?.message || "Could not remove document");
    }
  };

  // The endpoint is authenticated (Bearer token), so a plain <a href> can't
  // hit it directly - fetch the blob through axios (auth header attached by
  // axiosBaseQuery, same as every other call), then hand the browser a
  // temporary object URL to open/download. Revoked immediately after use so
  // this never leaks memory across a long Employee Master session.
  const handleDownload = async (doc) => {
    setDownloadingId(doc.recordGuid);
    try {
      const blob = await fetchContent({ recordGuid, documentRecordGuid: doc.recordGuid }).unwrap();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = doc.originalFileName || "document";
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      toast.error(error?.message || "Could not open document");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h5 className="mb-3">Documents</h5>

        {canManage && (
          <div className="d-flex gap-2 mb-3 flex-wrap align-items-center">
            <select
              className="form-control"
              style={{ maxWidth: 200 }}
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
            >
              {DOCUMENT_TYPES.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <input
              type="file"
              className="form-control"
              style={{ maxWidth: 320 }}
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
            <button type="button" className="btn btn-outline-primary btn-sm" onClick={handleUpload} disabled={isUploading || !selectedFile}>
              <Upload size={14} className="me-1" /> {isUploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        )}

        {isLoading && <p className="text-muted small">Loading...</p>}
        {!isLoading && documents.length === 0 && <p className="text-muted small">No documents attached yet.</p>}

        {documents.length > 0 && (
          <div className="table-responsive">
            <table className="table table-sm mb-0">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>File</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {documents.map((doc) => (
                  <tr key={doc.recordGuid}>
                    <td><span className="badge bg-light text-dark border">{doc.documentType}</span></td>
                    <td>
                      <FileText size={14} className="me-1 text-muted" />
                      {doc.originalFileName}
                    </td>
                    <td>{formatBytes(doc.fileSizeBytes)}</td>
                    <td>{doc.uploadedDate ? dayjs(doc.uploadedDate).format("DD MMM YYYY, HH:mm") : ""}</td>
                    <td className="text-end">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-1"
                        onClick={() => handleDownload(doc)}
                        disabled={downloadingId === doc.recordGuid}
                        title="Download"
                      >
                        <Download size={16} />
                      </button>
                      {canManage && (
                        <button
                          type="button"
                          className="btn btn-link btn-sm p-1 text-danger"
                          onClick={() => handleDelete(doc.recordGuid)}
                          title="Remove"
                        >
                          <Trash2 size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDocuments;
